import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';

import { Plus, Target, ShieldAlert, Send } from 'lucide-react';

const TradesTab = ({
  trades,
  tradeStats,
  showTradeForm,
  setShowTradeForm,
  newTrade,
  setNewTrade,
  hasPermission,
  fetchTrades,
  API_URL
}) => {
  return (
          <div className="space-y-6">
            {/* Trade Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Total Trades</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{tradeStats?.total_trades || 0}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Active Trades</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">{tradeStats?.active_trades || 0}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Success Rate</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-blue-600">{tradeStats?.success_rate || 0}%</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Avg P&L</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold ${(tradeStats?.average_pl || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(tradeStats?.average_pl || 0) >= 0 ? '+' : ''}{tradeStats?.average_pl || 0}%
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Create New Trade Button */}
            {hasPermission('create_trades') && (
              <Button onClick={() => setShowTradeForm(!showTradeForm)}>
                <Plus className="w-4 h-4 mr-2" />
                {showTradeForm ? 'Hide Form' : 'Create New Trade'}
              </Button>
            )}

            {/* Create Trade Form */}
            {showTradeForm && (
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle>Create New Trade Signal</CardTitle>
                  <CardDescription>Post a new trade signal to Telegram groups</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Symbol */}
                    <div>
                      <Label>Symbol *</Label>
                      <Input
                        placeholder="NIFTY, BANKNIFTY, RELIANCE"
                        value={newTrade.symbol}
                        onChange={(e) => setNewTrade({...newTrade, symbol: e.target.value.toUpperCase()})}
                      />
                    </div>

                    {/* Trade Type */}
                    <div>
                      <Label>Trade Type *</Label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        value={newTrade.trade_type}
                        onChange={(e) => setNewTrade({...newTrade, trade_type: e.target.value})}
                      >
                        <option value="options">Options</option>
                        <option value="futures">Futures</option>
                        <option value="cash">Cash/Equity</option>
                      </select>
                    </div>

                    {/* Option Type (only for options) */}
                    {newTrade.trade_type === 'options' && (
                      <>
                        <div>
                          <Label>Option Type *</Label>
                          <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            value={newTrade.option_type}
                            onChange={(e) => setNewTrade({...newTrade, option_type: e.target.value})}
                          >
                            <option value="CE">CE (Call)</option>
                            <option value="PE">PE (Put)</option>
                          </select>
                        </div>
                        <div>
                          <Label>Strike Price *</Label>
                          <Input
                            type="number"
                            placeholder="19500"
                            value={newTrade.strike_price}
                            onChange={(e) => setNewTrade({...newTrade, strike_price: e.target.value})}
                          />
                        </div>
                      </>
                    )}

                    {/* Expiry Date (for options and futures) */}
                    {(newTrade.trade_type === 'options' || newTrade.trade_type === 'futures') && (
                      <div>
                        <Label>Expiry Date *</Label>
                        <Input
                          type="date"
                          value={newTrade.expiry_date}
                          onChange={(e) => setNewTrade({...newTrade, expiry_date: e.target.value})}
                        />
                      </div>
                    )}

                    {/* CMP Price */}
                    <div>
                      <Label>CMP (Current Market Price) *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="150.50"
                        value={newTrade.cmp_price}
                        onChange={(e) => setNewTrade({...newTrade, cmp_price: e.target.value})}
                      />
                    </div>

                    {/* Entry Price */}
                    <div>
                      <Label>Entry Price *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="150.00"
                        value={newTrade.entry_price}
                        onChange={(e) => setNewTrade({...newTrade, entry_price: e.target.value})}
                      />
                    </div>

                    {/* Stop Loss */}
                    <div>
                      <Label>Stop Loss *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="140.00"
                        value={newTrade.stop_loss}
                        onChange={(e) => setNewTrade({...newTrade, stop_loss: e.target.value})}
                      />
                    </div>
                  </div>

                  {/* Targets */}
                  <div className="mt-4">
                    <Label>Targets *</Label>
                    {newTrade.targets.map((target, index) => (
                      <div key={index} className="flex gap-2 mt-2">
                        <Input
                          placeholder={`Target ${index + 1} Price`}
                          type="number"
                          step="0.01"
                          value={target.price}
                          onChange={(e) => {
                            const updatedTargets = [...newTrade.targets];
                            updatedTargets[index] = {target_number: index + 1, price: e.target.value};
                            setNewTrade({...newTrade, targets: updatedTargets});
                          }}
                        />
                        {index > 0 && (
                          <Button 
                            variant="destructive" 
                            size="sm"
                            onClick={() => {
                              const updatedTargets = newTrade.targets.filter((_, i) => i !== index);
                              setNewTrade({...newTrade, targets: updatedTargets});
                            }}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => setNewTrade({
                        ...newTrade,
                        targets: [...newTrade.targets, {target_number: newTrade.targets.length + 1, price: ''}]
                      })}
                    >
                      + Add Target
                    </Button>
                  </div>

                  {/* Visible To */}
                  <div className="mt-4">
                    <Label>Send To (Telegram Groups)</Label>
                    <div className="flex gap-4 mt-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={newTrade.visible_to.includes('options_only')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewTrade({...newTrade, visible_to: [...newTrade.visible_to, 'options_only']});
                            } else {
                              setNewTrade({...newTrade, visible_to: newTrade.visible_to.filter(v => v !== 'options_only')});
                            }
                          }}
                          className="mr-2"
                        />
                        Options Only
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={newTrade.visible_to.includes('complete_package')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewTrade({...newTrade, visible_to: [...newTrade.visible_to, 'complete_package']});
                            } else {
                              setNewTrade({...newTrade, visible_to: newTrade.visible_to.filter(v => v !== 'complete_package')});
                            }
                          }}
                          className="mr-2"
                        />
                        Complete Package
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={newTrade.visible_to.includes('equity_only')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewTrade({...newTrade, visible_to: [...newTrade.visible_to, 'equity_only']});
                            } else {
                              setNewTrade({...newTrade, visible_to: newTrade.visible_to.filter(v => v !== 'equity_only')});
                            }
                          }}
                          className="mr-2"
                        />
                        Equity Only
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Leave unchecked to auto-determine based on trade type</p>
                  </div>

                  {/* Auto Expire */}
                  <div className="mt-4">
                    <Label>Auto-Expire After (Hours)</Label>
                    <Input
                      type="number"
                      value={newTrade.auto_expire_hours}
                      onChange={(e) => setNewTrade({...newTrade, auto_expire_hours: parseInt(e.target.value)})}
                      placeholder="72"
                    />
                    <p className="text-xs text-gray-500 mt-1">Trade will automatically expire after this duration</p>
                  </div>

                  {/* Submit Button */}
                  <div className="mt-6 flex gap-2">
                    <Button onClick={async () => {
                      try {
                        // Validate required fields
                        if (!newTrade.symbol || !newTrade.cmp_price || !newTrade.entry_price || !newTrade.stop_loss) {
                          alert('Please fill all required fields');
                          return;
                        }

                        if (newTrade.targets.some(t => !t.price)) {
                          alert('Please fill all target prices');
                          return;
                        }

                        const response = await fetch(`${API_URL}/api/admin/trade-signals/create`, {
                          method: 'POST',
                          headers: {'Content-Type': 'application/json'},
                          body: JSON.stringify({
                            ...newTrade,
                            cmp_price: parseFloat(newTrade.cmp_price),
                            entry_price: parseFloat(newTrade.entry_price),
                            stop_loss: parseFloat(newTrade.stop_loss),
                            strike_price: newTrade.strike_price ? parseFloat(newTrade.strike_price) : null,
                            targets: newTrade.targets.map(t => ({
                              target_number: t.target_number,
                              price: parseFloat(t.price)
                            }))
                          })
                        });

                        if (response.ok) {
                          alert('✅ Trade signal created and posted to Telegram!');
                          setShowTradeForm(false);
                          setNewTrade({
                            symbol: '',
                            trade_type: 'options',
                            option_type: 'CE',
                            strike_price: '',
                            expiry_date: '',
                            cmp_price: '',
                            entry_price: '',
                            stop_loss: '',
                            targets: [{target_number: 1, price: ''}],
                            visible_to: [],
                            auto_expire_hours: 72
                          });
                          // Refresh trades list
                          fetchTrades();
                        } else {
                          const error = await response.json();
                          alert(`Failed to create trade: ${error.detail}`);
                        }
                      } catch (error) {
                        console.error('Error creating trade:', error);
                        alert('Error creating trade signal');
                      }
                    }}>
                      <Send className="w-4 h-4 mr-2" />
                      Post Trade Signal
                    </Button>
                    <Button variant="outline" onClick={() => setShowTradeForm(false)}>
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Active Trades List */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle>Active Trades</CardTitle>
                <CardDescription>Manage your active trade signals</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {trades.filter(t => t.status === 'active').map((trade) => (
                    <div key={trade.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-lg font-bold">{trade.symbol}</h3>
                          <div className="flex gap-2 mt-1">
                            <Badge>{trade.trade_type.toUpperCase()}</Badge>
                            {trade.option_type && <Badge variant="outline">{trade.option_type}</Badge>}
                            <Badge variant="outline">Active</Badge>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-500">Created: {new Date(trade.created_at).toLocaleString()}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-4 gap-4 mt-4">
                        <div>
                          <p className="text-xs text-gray-500">Entry</p>
                          <p className="font-bold">₹{trade.entry_price}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">CMP</p>
                          <p className="font-bold">₹{trade.cmp_price}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Stop Loss</p>
                          <p className="font-bold text-red-600">₹{trade.stop_loss}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Targets</p>
                          <p className="font-bold text-green-600">{trade.targets.length} Targets</p>
                        </div>
                      </div>

                      <div className="flex gap-2 mt-4">
                        <Button size="sm" variant="outline" onClick={async () => {
                          const exitPrice = prompt('Enter exit price:');
                          if (!exitPrice) return;

                          try {
                            const response = await fetch(`${API_URL}/api/admin/trade-signals/${trade.id}/status`, {
                              method: 'PUT',
                              headers: {'Content-Type': 'application/json'},
                              body: JSON.stringify({
                                status: 'target_hit',
                                exit_price: parseFloat(exitPrice),
                                exit_reason: 'target_achieved'
                              })
                            });

                            if (response.ok) {
                              alert('✅ Trade marked as Target Hit!');
                              fetchTrades();
                            }
                          } catch (error) {
                            console.error('Error updating trade:', error);
                          }
                        }}>
                          <Target className="w-3 h-3 mr-1" />
                          Target Hit
                        </Button>
                        
                        <Button size="sm" variant="outline" onClick={async () => {
                          const exitPrice = prompt('Enter exit price:');
                          if (!exitPrice) return;

                          try {
                            const response = await fetch(`${API_URL}/api/admin/trade-signals/${trade.id}/status`, {
                              method: 'PUT',
                              headers: {'Content-Type': 'application/json'},
                              body: JSON.stringify({
                                status: 'sl_hit',
                                exit_price: parseFloat(exitPrice),
                                exit_reason: 'stop_loss'
                              })
                            });

                            if (response.ok) {
                              alert('✅ Trade marked as SL Hit!');
                              fetchTrades();
                            }
                          } catch (error) {
                            console.error('Error updating trade:', error);
                          }
                        }}>
                          <ShieldAlert className="w-3 h-3 mr-1" />
                          SL Hit
                        </Button>

                        <Button size="sm" variant="outline" onClick={async () => {
                          const exitPrice = prompt('Enter exit price:');
                          if (!exitPrice) return;

                          try {
                            const response = await fetch(`${API_URL}/api/admin/trade-signals/${trade.id}/status`, {
                              method: 'PUT',
                              headers: {'Content-Type': 'application/json'},
                              body: JSON.stringify({
                                status: 'exited',
                                exit_price: parseFloat(exitPrice),
                                exit_reason: 'manual_exit'
                              })
                            });

                            if (response.ok) {
                              alert('✅ Trade exited!');
                              fetchTrades();
                            }
                          } catch (error) {
                            console.error('Error updating trade:', error);
                          }
                        }}>
                          Exit Manually
                        </Button>
                      </div>
                    </div>
                  ))}
                  
                  {trades.filter(t => t.status === 'active').length === 0 && (
                    <p className="text-center text-gray-500 py-8">No active trades</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Send Performance Report */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle>Send Performance Report</CardTitle>
                <CardDescription>Manually send performance update to subscribers</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label>Performance Message</Label>
                    <textarea
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      rows="4"
                      placeholder="Enter performance report message..."
                      id="performance-message"
                    ></textarea>
                  </div>

                  <div>
                    <Label>Send To</Label>
                    <div className="flex gap-4 mt-2">
                      <label className="flex items-center">
                        <input type="checkbox" className="mr-2" id="perf-options" />
                        Options Only
                      </label>
                      <label className="flex items-center">
                        <input type="checkbox" className="mr-2" id="perf-complete" />
                        Complete Package
                      </label>
                      <label className="flex items-center">
                        <input type="checkbox" className="mr-2" id="perf-equity" />
                        Equity Only
                      </label>
                    </div>
                  </div>

                  <Button onClick={async () => {
                    const message = document.getElementById('performance-message').value;
                    const groups = [];
                    if (document.getElementById('perf-options').checked) groups.push('options_only');
                    if (document.getElementById('perf-complete').checked) groups.push('complete_package');
                    if (document.getElementById('perf-equity').checked) groups.push('equity_only');

                    if (!message || groups.length === 0) {
                      alert('Please enter message and select at least one group');
                      return;
                    }

                    try {
                      const response = await fetch(`${API_URL}/api/admin/trade-signals/send-performance`, {
                        method: 'POST',
                        headers: {'Content-Type': 'application/json'},
                        body: JSON.stringify({ message, groups })
                      });

                      if (response.ok) {
                        alert('✅ Performance report sent to Telegram!');
                        document.getElementById('performance-message').value = '';
                        document.getElementById('perf-options').checked = false;
                        document.getElementById('perf-complete').checked = false;
                        document.getElementById('perf-equity').checked = false;
                      }
                    } catch (error) {
                      console.error('Error sending performance:', error);
                      alert('Failed to send performance report');
                    }
                  }}>
                    <Send className="w-4 h-4 mr-2" />
                    Send Performance Report
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

  );
};

export default TradesTab;
